import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
// import Icon, { IconWrapper } from "@insite/mobius/Icon"; // Custom Code
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

/* Custom Code - Start */
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import useExpandProps from "@store/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import AlertCircleFill from "@store/Library/Components/Icons/AlertCircle/AlertCircleFill";
import AlertTriangleFill from "@store/Library/Components/Icons/AlertTriangle/AlertTriangleFill";
import CheckmarkCircle2Fill from "@store/Library/Components/Icons/CheckmarkCircle2/CheckmarkCircle2Fill";
import InfoFill from "@store/Library/Components/Icons/Info/InfoFill";
import Loader from "@store/Library/Components/Toolkit/Loader";
import { linkAltStyles } from "@store/Themes/common/mixins/link";
import { StatusColor, statusStyles } from "@store/Themes/common/mixins/status";
import { loadingDotsStyles } from "@store/Themes/common/mixins/typography";
/* Custom Code - End */

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    // Custom Code
    overridedStyles?: ProductAvailabilityStyles;
    // Store Custom Code
    isDiscontinuedCustom?: string | undefined;
    isOrderAsNeeded?: string | undefined;
    isLiquidation?: boolean;
    qtyOnHand?: number | undefined;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    isCurrentCartPickup: getCurrentCartState(state).value?.fulfillmentMethod === FulfillmentMethod.PickUp,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // Custom Code
    availabilityByWarehouseIcon?: IconPresentationProps;
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-flow: row wrap;
        `,
    },
    availabilityByWarehouseLink: {
        icon: {
            position: "left",
            iconProps: {
                src: InfoFill,
                size: 18,
                css: css`
                    margin-right: ${pxToRem(5)}; /* Store Custom Code */
                    color: inherit;
                `,
            },
        },
        css: css`
            ${linkAltStyles()}
            margin-left: ${pxToRem(5)};
        `,
    },
    availabilityByWarehouseModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalContent: css`
                padding: ${pxToRem(10)};
            `,
        },
    },
    availabilityByWarehouseTable: {
        css: css`
            ${x => x.theme.dataTable.defaultProps?.css}
            border: none;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        css: loadingDotsStyles(),
    },
};

/* Custom Code - Start */
const InventoryMessage = styled(StyledWrapper)<{ _color: StatusColor }>`
    ${x => statusStyles(x._color)}
`;
/* Custom Code - End */

// Custom Code - next line - return type: StatusColor
const getAvailabilityColor = (
    productSettings: ProductSettingsModel,
    availability?: AvailabilityDto,
    // Store Custom Code
    isDiscontinuedCustom?: string | undefined,
    isOrderAsNeeded?: string | undefined,
    isLiquidation?: boolean,
    isCurrentCartPickup?: boolean,
    qtyOnHand?: number | undefined,
): StatusColor => {
    const qtyOnHandToCheck = qtyOnHand ?? 0;
    const availabilityType = availability?.messageType;
    const isQtyOutOfStock = qtyOnHand === 0;
    const isAvailabilityOutOfStock = availabilityType === AvailabilityMessageType.OutOfStock;
    const isOutOfStock = isQtyOutOfStock || (qtyOnHand === undefined && isAvailabilityOutOfStock);

    if (!productSettings.showInventoryAvailability) {
        console.log("showInventoryAvailability");
        return "neutral"; // Custom Code
    }

    if (isLiquidation && isOutOfStock) {
        return "danger";
    }

    if (isDiscontinuedCustom?.toLowerCase() === "true" && qtyOnHandToCheck === 0 /* Store Custom Code */) {
        return "danger";
    }

    if (isLiquidation && isCurrentCartPickup) {
        return "success";
    }

    /* Store Custom Code - Start */
    if (
        availabilityType === AvailabilityMessageType.OutOfStock ||
        availabilityType === AvailabilityMessageType.LowStock
    ) {
        return "warning";
    }
    /* Store Custom Code - End */

    return "success";
};

const getAvailabilityIcon = (
    productSettings: ProductSettingsModel,
    availability?: AvailabilityDto,
    // Store Custom Code
    isDiscontinuedCustom?: string | undefined,
    isOrderAsNeeded?: string | undefined,
    isLiquidation?: boolean,
    isCurrentCartPickup?: boolean,
    qtyOnHand?: number | undefined,
) => {
    /* Store Custom Code - Start */
    const qtyOnHandToCheck = qtyOnHand ?? 0;
    const availabilityType = availability?.messageType;
    const isQtyOutOfStock = qtyOnHand === 0;
    const isAvailabilityOutOfStock = availabilityType === AvailabilityMessageType.OutOfStock;
    const isOutOfStock = isQtyOutOfStock || (qtyOnHand === undefined && isAvailabilityOutOfStock);

    if (isLiquidation && isOutOfStock) {
        return AlertTriangleFill;
    }
    
    if (isDiscontinuedCustom?.toLowerCase() === "true" && qtyOnHandToCheck === 0 /* Store Custom Code */) {
        return AlertTriangleFill; // Custom Code
    }

    if (isLiquidation && isCurrentCartPickup) {
        return CheckmarkCircle2Fill;
    }

    /* Store Custom Code - End */
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    if (
        availabilityType === AvailabilityMessageType.OutOfStock ||
        availabilityType === AvailabilityMessageType.LowStock // Store Custom Code
    ) {
        return AlertCircleFill; // Custom Code
    }

    return CheckmarkCircle2Fill; // Custom Code
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    // Custom Code
    overridedStyles,
    // Store Custom Code
    isDiscontinuedCustom,
    isOrderAsNeeded,
    isLiquidation,
    isCurrentCartPickup,
    qtyOnHand,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses", "withAllInventories"] as any, // Store Custom Code
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const color = getAvailabilityColor(
        productSettings,
        availability,
        // Store Custom Code
        isDiscontinuedCustom,
        isOrderAsNeeded,
        isLiquidation,
        isCurrentCartPickup,
        qtyOnHand,
    );
    const iconSrc = getAvailabilityIcon(
        productSettings,
        availability,
        // Store Custom Code
        isDiscontinuedCustom,
        isOrderAsNeeded,
        isLiquidation,
        isCurrentCartPickup,
        qtyOnHand,
    );

    const styles = useExpandProps(productAvailabilityStyles, extendedStyles, overridedStyles); // Custom Code
    if (!showInventory) {
        return null;
    }

    /* Custom Code - Start */
    const unavailableMessage = (
        <InventoryMessage _color="neutral" {...styles.inventoryMessage}>
            <Icon color="neutral" src={AlertTriangleFill} />
            <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                {siteMessage("RealTimeInventory_InventoryLoadFailed")}
            </Typography>
        </InventoryMessage>
    );
    /* Custom Code - End */

    if (failedToLoadInventory) {
        return <StyledWrapper {...styles.wrapper}>{unavailableMessage}</StyledWrapper>; // Custom Code
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        /* Store Custom Code - Start */
        let availabilityMessage = availability.message;
        const isQtyOutOfStock = qtyOnHand === 0;
        const isAvailabilityOutOfStock = availability.messageType === AvailabilityMessageType.OutOfStock;
        const isOutOfStock = isQtyOutOfStock || (qtyOnHand === undefined && isAvailabilityOutOfStock);
        if (isLiquidation && isOutOfStock) {
            availabilityMessage = translate("Product_Discontinued");
        } else if (isLiquidation && isCurrentCartPickup) {
            availabilityMessage = translate("In Stock");
        }

        inventoryTextComponent = (
            <InventoryMessage _color={color} {...styles.inventoryMessage /* Custom Code - _color */}>
                <Icon color={color} src={iconSrc} />
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {availabilityMessage /* Store Custom Code */}
                </Typography>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = unavailableMessage; // Custom Code
    }

    /* Removed - Grid */
    return (
        <StyledWrapper {...styles.wrapper /* Custom Code */}>
            {inventoryTextComponent /* Custom Code */}
            {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
                <>
                    {/* Store Custom Code - Start */}
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                        title={translate("View Availability by Warehouse") /* Custom Code */}
                    >
                        {translate("Check in-store availability")}
                    </Link>
                    {/* Store Custom Code - End */}
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("In-store availability")} /* Store Custom Code */
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && <Loader /> /* Custom Code */}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <DataTable
                                        {...styles.availabilityByWarehouseTable}
                                        data-test-selector="availabilityByWarehouseModal"
                                    >
                                        <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Warehouse")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("QTY")}
                                            </DataTableHeader>
                                        </DataTableHead>
                                        <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                            {warehouses?.map(warehouse => (
                                                <DataTableRow
                                                    {...styles.availabilityByWarehouseTableRow}
                                                    key={warehouse.name}
                                                >
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Description"
                                                    >
                                                        {warehouse.description || warehouse.name}
                                                    </DataTableCell>
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Quantity"
                                                    >
                                                        {warehouse.qty}
                                                    </DataTableCell>
                                                </DataTableRow>
                                            ))}
                                        </DataTableBody>
                                    </DataTable>
                                )}
                            </>
                        )}
                    </Modal>
                </>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
